import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power } from 'lucide-react';
import { positionsApi } from '../../../api/admin/positions';
import { departmentsApi } from '../../../api/admin/departments';
import { genericDocumentApi } from '../../../api/generic-documents';
import { ApiError } from '../../../api/client';
import { PageHeader } from '../../../components/PageHeader';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { WorkflowTrackingTab } from '../../../components/WorkflowTrackingTab';
import { POSITION_STAGES } from '../../../config/workflow-stages';
import { WorkspaceTabs } from '../../../components/WorkspaceTabs';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

export function PositionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const positionId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('admin.positions.manage');

  const listQuery = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list() });
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const docsQuery = useQuery({ queryKey: ['position-documents', positionId], queryFn: () => genericDocumentApi.listFor('position', positionId) });

  const deactivateMutation = useMutation({
    mutationFn: () => positionsApi.deactivate(positionId),
    onSuccess: () => { showSuccess('Position deactivated.'); queryClient.invalidateQueries({ queryKey: ['positions'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate position.'),
  });

  const departmentName = (deptId: number | null) => departmentsQuery.data?.data.find((d) => d.id === deptId)?.name ?? '-';

  if (!listQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const p = listQuery.data.data.find((pos) => pos.id === positionId);
  if (!p) return <div className="page"><p className="muted">Position not found.</p></div>;

  return (
    <div className="page">
      <Link to="/admin/positions">&larr; Back to Positions</Link>
      <PageHeader
        title={p.title}
        actions={canManage && p.is_active && <button className="btn-secondary" onClick={() => deactivateMutation.mutate()}><Power size={16} className="inline-icon" /> Deactivate</button>}
        readOnly={!canManage}
      />
      <p className="muted">{departmentName(p.department_id)} · {p.is_active ? 'Active' : 'Inactive'}</p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: <CommentsThread entityType="position" entityId={positionId} />,
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('position', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('position', positionId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('position', documentId)}
                queryKeyToInvalidate={['position-documents', positionId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="position" entityId={positionId} actionStageMap={POSITION_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
